import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { 
  apiService, 
  AffiliateMember, 
  AffiliateMemberStatus,
  AffiliateCommission,
  AffiliateCommissionStatus,
  AffiliateAdminNote,
  AffiliateWithdrawal,
  AffiliateWithdrawalStatus
} from '../services/api';

interface AffiliateMemberViewPageProps {
  memberId: number;
  onBack: () => void;
}

const AffiliateMemberViewPage: React.FC<AffiliateMemberViewPageProps> = ({
  memberId,
  onBack,
}) => {
  const [member, setMember] = useState<AffiliateMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'commission' | 'withdrawal'>('commission');
  const [newNote, setNewNote] = useState('');
  type CommissionSummary = {
    totalEarned: number;
    totalPending: number;
    totalConfirmed: number;
  };

  const [commissions, setCommissions] = useState<AffiliateCommission[]>([]);
  const [commissionSummary, setCommissionSummary] = useState<CommissionSummary>({
    totalEarned: 0,
    totalPending: 0,
    totalConfirmed: 0,
  });
  const [commissionStatusFilter, setCommissionStatusFilter] = useState<'ALL' | AffiliateCommissionStatus>('ALL');
  const [commissionSearch, setCommissionSearch] = useState<string>('');
  const [commissionDateSort, setCommissionDateSort] = useState<'asc' | 'desc'>('desc');
  const [commissionStatusSort, setCommissionStatusSort] = useState<'asc' | 'desc' | null>(null);
  const [commissionPageSize, setCommissionPageSize] = useState<number>(10);
  const [commissionPage, setCommissionPage] = useState<number>(1);
  const [commissionsLoading, setCommissionsLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState<AffiliateAdminNote[]>([]);
  const [adminNotesLoading, setAdminNotesLoading] = useState(true);
  const [savingNote, setSavingNote] = useState(false);
  const [withdrawals, setWithdrawals] = useState<AffiliateWithdrawal[]>([]);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(true);
  const [withdrawalStatusFilter, setWithdrawalStatusFilter] = useState<'ALL' | AffiliateWithdrawalStatus>('ALL');
  const [withdrawalSearch, setWithdrawalSearch] = useState<string>('');
  const [withdrawalDateSort, setWithdrawalDateSort] = useState<'asc' | 'desc'>('desc');
  const [withdrawalStatusSort, setWithdrawalStatusSort] = useState<'asc' | 'desc' | null>(null);
  const [withdrawalPaidAtSort, setWithdrawalPaidAtSort] = useState<'asc' | 'desc' | null>(null);
  const [withdrawalPageSize, setWithdrawalPageSize] = useState<number>(10);
  const [withdrawalPage, setWithdrawalPage] = useState<number>(1);
  const [totalPaidOut, setTotalPaidOut] = useState<number | null>(null);

  // Get commission status display
  const getCommissionStatusDisplay = (status: AffiliateCommissionStatus): string => {
    switch (status) {
      case AffiliateCommissionStatus.PENDING:
        return 'Pending';
      case AffiliateCommissionStatus.CONFIRMED:
        return 'Confirmed';
      case AffiliateCommissionStatus.PAID:
        return 'Paid';
      case AffiliateCommissionStatus.VOID:
        return 'Void';
      default:
        return status;
    }
  };

  const getWithdrawalStatusDisplay = (status: AffiliateWithdrawalStatus): string => {
    switch (status) {
      case AffiliateWithdrawalStatus.REQUESTED:
        return 'Requested';
      case AffiliateWithdrawalStatus.APPROVED:
        return 'Approved';
      case AffiliateWithdrawalStatus.REJECTED:
        return 'Rejected';
      case AffiliateWithdrawalStatus.PAID:
        return 'Paid';
      case AffiliateWithdrawalStatus.CANCELLED:
        return 'Cancelled';
      default:
        return status;
    }
  };

  useEffect(() => {
    // Update URL hash when component mounts or memberId changes
    if (memberId) {
      window.location.hash = `#affiliate-member-view-${memberId}`;
    }
  }, [memberId]);

  useEffect(() => {
    const fetchMember = async () => {
      setLoading(true);
      try {
        const data = await apiService.getAffiliateMember(memberId);
        setMember(data);
      } catch (error) {
        console.error('Failed to fetch affiliate member:', error);
      } finally {
        setLoading(false);
      }
    };

    if (memberId) {
      fetchMember();
    }
  }, [memberId]);

  useEffect(() => {
    const fetchCommissions = async () => {
      setCommissionsLoading(true);
      try {
        const data = await apiService.getAffiliateCommissionsByMemberId(memberId);
        setCommissions(data.commissions);
        setCommissionSummary(data.summary);
      } catch (error) {
        console.error('Failed to fetch affiliate commissions:', error);
      } finally {
        setCommissionsLoading(false);
      }
    };

    if (memberId) {
      fetchCommissions();
    }
  }, [memberId]);

  const filteredCommissions = useMemo(() => {
    const search = commissionSearch.trim().toLowerCase();

    const filtered = commissions.filter((c) => {
      if (commissionStatusFilter !== 'ALL' && c.status !== commissionStatusFilter) {
        return false;
      }

      if (search) {
        const haystack = [
          c.order?.orderNumber,
          c.order?.product?.name,
          c.program?.name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(search)) return false;
      }
      return true;
    });

    let sorted = filtered;
    
    // Sort by status if status sort is active
    if (commissionStatusSort !== null) {
      sorted = [...sorted].sort((a, b) => {
        const statusA = getCommissionStatusDisplay(a.status);
        const statusB = getCommissionStatusDisplay(b.status);
        if (commissionStatusSort === 'asc') {
          return statusA.localeCompare(statusB);
        } else {
          return statusB.localeCompare(statusA);
        }
      });
    } else {
      // Sort by date if status sort is not active
      sorted = sorted.sort((a, b) => {
        const da = new Date(a.createdAt).getTime();
        const db = new Date(b.createdAt).getTime();
        return commissionDateSort === 'asc' ? da - db : db - da;
      });
    }
    
    return sorted;
  }, [commissions, commissionStatusFilter, commissionSearch, commissionDateSort, commissionStatusSort]);

  const totalCommissionPages = Math.max(1, Math.ceil(filteredCommissions.length / commissionPageSize));

  const filteredWithdrawals = useMemo(() => {
    const search = withdrawalSearch.trim().toLowerCase();
    const filtered = withdrawals.filter((w) => {
      if (withdrawalStatusFilter !== 'ALL' && w.status !== withdrawalStatusFilter) {
        return false;
      }
      if (search) {
        const haystack = [
          w.id?.toString(),
          w.payoutMethod,
          w.status,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
    let sorted = filtered;
    
    // Sort by status if status sort is active
    if (withdrawalStatusSort !== null) {
      sorted = [...sorted].sort((a, b) => {
        const statusA = getWithdrawalStatusDisplay(a.status);
        const statusB = getWithdrawalStatusDisplay(b.status);
        if (withdrawalStatusSort === 'asc') {
          return statusA.localeCompare(statusB);
        } else {
          return statusB.localeCompare(statusA);
        }
      });
    } else if (withdrawalPaidAtSort !== null) {
      // Sort by paid at if paid at sort is active
      sorted = [...sorted].sort((a, b) => {
        const da = a.paidAt ? new Date(a.paidAt).getTime() : 0;
        const db = b.paidAt ? new Date(b.paidAt).getTime() : 0;
        if (withdrawalPaidAtSort === 'asc') {
          return da - db;
        } else {
          return db - da;
        }
      });
    } else {
      // Sort by date if status sort and paid at sort are not active
      sorted = sorted.sort((a, b) => {
        const da = new Date(a.requestedAt || a.createdAt).getTime();
        const db = new Date(b.requestedAt || b.createdAt).getTime();
        return withdrawalDateSort === 'asc' ? da - db : db - da;
      });
    }
    
    return sorted;
  }, [withdrawals, withdrawalStatusFilter, withdrawalSearch, withdrawalDateSort, withdrawalStatusSort, withdrawalPaidAtSort]);

  const pagedWithdrawals = useMemo(() => {
    const start = (withdrawalPage - 1) * withdrawalPageSize;
    return filteredWithdrawals.slice(start, start + withdrawalPageSize);
  }, [filteredWithdrawals, withdrawalPage, withdrawalPageSize]);

  const totalWithdrawalPages = Math.max(1, Math.ceil(filteredWithdrawals.length / withdrawalPageSize));

  useEffect(() => {
    const fetchAdminNotes = async () => {
      setAdminNotesLoading(true);
      try {
        const notes = await apiService.getAffiliateAdminNotesByMemberId(memberId);
        setAdminNotes(notes);
      } catch (error) {
        console.error('Failed to fetch admin notes:', error);
      } finally {
        setAdminNotesLoading(false);
      }
    };

    if (memberId) {
      fetchAdminNotes();
    }
  }, [memberId]);

  useEffect(() => {
    const fetchWithdrawals = async () => {
      setWithdrawalsLoading(true);
      try {
        const data = await apiService.getAffiliateWithdrawalsByMemberId(memberId);
        setWithdrawals(data.withdrawals);
        setTotalPaidOut(data.totalPaidOut ?? null);
      } catch (error) {
        console.error('Failed to fetch withdrawals:', error);
      } finally {
        setWithdrawalsLoading(false);
      }
    };

    if (memberId) {
      fetchWithdrawals();
    }
  }, [memberId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: AffiliateMemberStatus) => {
    switch (status) {
      case AffiliateMemberStatus.ACTIVE:
        return 'bg-green-100 text-green-800';
      case AffiliateMemberStatus.INACTIVE:
        return 'bg-gray-100 text-gray-800';
      case AffiliateMemberStatus.BANNED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleSaveNote = async () => {
    if (!newNote.trim()) return;
    
    // Get current admin from localStorage
    const userData = localStorage.getItem('user');
    if (!userData) {
      console.error('No user data found');
      return;
    }

    try {
      const user = JSON.parse(userData);
      if (!user.id) {
        console.error('No admin ID found');
        return;
      }

      setSavingNote(true);
      const createdNote = await apiService.createAffiliateAdminNote({
        affiliateMemberId: memberId,
        adminId: user.id,
        note: newNote.trim(),
      });

      setAdminNotes([createdNote, ...adminNotes]);
      setNewNote('');
    } catch (error) {
      console.error('Failed to save admin note:', error);
      alert('Failed to save note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  const getCommissionStatusColor = (status: AffiliateCommissionStatus) => {
    switch (status) {
      case AffiliateCommissionStatus.PAID:
        return 'bg-green-100 text-green-800';
      case AffiliateCommissionStatus.CONFIRMED:
        return 'bg-blue-100 text-blue-800';
      case AffiliateCommissionStatus.VOID:
        return 'bg-red-100 text-red-800';
      case AffiliateCommissionStatus.PENDING:
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getWithdrawalStatusColor = (status: AffiliateWithdrawalStatus) => {
    switch (status) {
      case AffiliateWithdrawalStatus.PAID:
        return 'bg-green-100 text-green-800';
      case AffiliateWithdrawalStatus.APPROVED:
        return 'bg-blue-100 text-blue-800';
      case AffiliateWithdrawalStatus.REJECTED:
        return 'bg-red-100 text-red-800';
      case AffiliateWithdrawalStatus.CANCELLED:
        return 'bg-gray-100 text-gray-800';
      case AffiliateWithdrawalStatus.REQUESTED:
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleBack = () => {
    // Update URL hash before calling onBack
    window.location.hash = '#affiliate-member-management';
    onBack();
  };

  // Use dummy data if member data is not available
  const displayMember: AffiliateMember = member || {
    id: memberId,
    code: `AFF-${String(memberId).padStart(5, '0')}`,
    slug: 'https://example.com',
    walletBalance: 1250.75 * 15000,
    status: AffiliateMemberStatus.ACTIVE,
    createdAt: '2023-01-15',
    updatedAt: '2023-01-15',
    user: {
      id: 15,
      email: 'alice.johnson@example.com',
      name: 'Alice Johnson',
    },
  };


  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBack}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Affiliate Member Details</h1>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-imola-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={handleBack}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Affiliate Member Details</h1>
      </div>

      {/* Affiliate Member Overview */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 sm:p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 sm:mb-6 text-left">Affiliate Member Overview</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-4 sm:mb-6">
            {/* Kolom 1: Member Info */}
            <div className="flex items-start space-x-4 sm:space-x-6 lg:space-x-8">
            {/* Profile Picture */}
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {displayMember.user?.name ? (
                <span className="text-xl sm:text-2xl font-semibold text-gray-600">
                  {displayMember.user.name.charAt(0).toUpperCase()}
                </span>
              ) : (
                <span className="text-xl sm:text-2xl font-semibold text-gray-600">A</span>
              )}
            </div>

            {/* Member Info */}
            <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-2 sm:mb-3 gap-2">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 text-left truncate">
                  {displayMember.user?.name || 'Unknown User'}
                </h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(displayMember.status)}`}>
                  {displayMember.status}
                </span>
              </div>
                <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 text-left truncate">
                <span className="font-medium">ID:</span> {displayMember.code || `AFF-${String(displayMember.id).padStart(5, '0')}`}
              </p>
                <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 text-left truncate">
                  <span className="font-medium">Email:</span> {displayMember.user?.email || '-'}
              </p>
                <p className="text-xs sm:text-sm text-gray-600 text-left">
                <span className="font-medium">Join Date:</span> {formatDate(displayMember.createdAt)}
              </p>
            </div>
          </div>

            {/* Kolom 2: Current Balance dan Last Payout */}
            <div>
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 text-left">Total Commission Earned</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 text-left break-words">{formatCurrency(commissionSummary.totalEarned)}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 text-left">Total Confirmed</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900 text-left break-words">{formatCurrency(commissionSummary.totalConfirmed)}</p>
                </div>
              </div>
            </div>

          {/* Kolom 3: Total Pending & Total Paid Out */}
            <div>
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 text-left">Total Pending Commission</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 text-left break-words">{formatCurrency(commissionSummary.totalPending)}</p>
            </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 text-left">Total Paid Out</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 text-left break-words">{totalPaidOut !== null ? formatCurrency(totalPaidOut) : '-'}</p>
            </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Commission History */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6">
          {/* Tabs */}
          <div className="bg-gray-100 rounded-lg p-1 mb-6">
            <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => setActiveTab('commission')}
                className={`px-4 py-2.5 rounded-lg font-medium transition-all text-center ${
                activeTab === 'commission'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'bg-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Commission History
            </button>
            <button
              onClick={() => setActiveTab('withdrawal')}
                className={`px-4 py-2.5 rounded-lg font-medium transition-all text-center ${
                activeTab === 'withdrawal'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'bg-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Withdrawal History
            </button>
            </div>
          </div>

          {activeTab === 'commission' && (
            <>
              {commissionsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-imola-primary mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading commissions...</p>
                </div>
              ) : (
            <>
                  {/* Filters - search, status, page size */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 items-end">
                    <div className="md:col-span-2">
                      <label className="block text-sm text-gray-600 mb-1 text-left">Search</label>
                      <div className="flex items-center border border-gray-300 rounded-md px-3 py-2 bg-white">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                        </svg>
                        <input
                          type="text"
                          value={commissionSearch}
                          onChange={(e) => {
                            setCommissionSearch(e.target.value);
                            setCommissionPage(1);
                          }}
                          placeholder="Search by order ID or product..."
                          className="w-full ml-2 outline-none text-sm text-gray-800"
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1">
                        <label className="block text-sm text-gray-600 mb-1 text-left">Status</label>
                        <select
                          value={commissionStatusFilter}
                          onChange={(e) => {
                            setCommissionStatusFilter((e.target.value as 'ALL' | AffiliateCommissionStatus) || 'ALL');
                            setCommissionPage(1);
                          }}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary"
                        >
                          <option value="ALL">All</option>
                          <option value={AffiliateCommissionStatus.PENDING}>Pending</option>
                          <option value={AffiliateCommissionStatus.CONFIRMED}>Confirmed</option>
                          <option value={AffiliateCommissionStatus.PAID}>Paid</option>
                          <option value={AffiliateCommissionStatus.VOID}>Void</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm text-gray-600 mb-1 text-left">Page Size</label>
                        <select
                          value={commissionPageSize}
                          onChange={(e) => {
                            setCommissionPageSize(Number(e.target.value));
                            setCommissionPage(1);
                          }}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary"
                        >
                          {[5, 10, 20, 50].map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

              {/* Commissions Table */}
                  {filteredCommissions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No commission history available</p>
                    </div>
                  ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                          onClick={() => {
                            setCommissionDateSort(commissionDateSort === 'asc' ? 'desc' : 'asc');
                            setCommissionStatusSort(null);
                          }}
                        >
                          <span className="inline-flex items-center space-x-1">
                            <span>Date</span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 text-gray-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              {commissionDateSort === 'asc' ? (
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 15.75 12 9l6.75 6.75" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 8.25 12 15l6.75-6.75" />
                              )}
                            </svg>
                          </span>
                        </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Order</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comission</th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                        onClick={() => {
                          if (commissionStatusSort === null) {
                            setCommissionStatusSort('asc');
                            setCommissionDateSort('desc');
                          } else if (commissionStatusSort === 'asc') {
                            setCommissionStatusSort('desc');
                          } else {
                            setCommissionStatusSort(null);
                            setCommissionDateSort('desc');
                          }
                        }}
                      >
                        <span className="inline-flex items-center space-x-1">
                          <span>Status</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            {commissionStatusSort === 'asc' ? (
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 15.75 12 9l6.75 6.75" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 8.25 12 15l6.75-6.75" />
                            )}
                          </svg>
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                     {filteredCommissions.map((commission) => (
                      <tr key={commission.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono text-left">
                                {commission.order?.orderNumber}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                                {formatDate(commission.createdAt)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                                {commission.order?.product?.name || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                                {formatCurrency(Number(commission.order?.totalAmount))}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-left">
                                {formatCurrency(Number(commission.commissionAmount))}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-left">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCommissionStatusColor(commission.status)}`}>
                                  {getCommissionStatusDisplay(commission.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                  )}
                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
                    <div>
                      Showing {Math.min((commissionPage - 1) * commissionPageSize + 1, filteredCommissions.length)}-
                      {Math.min(commissionPage * commissionPageSize, filteredCommissions.length)} of {filteredCommissions.length}
                    </div>
                    <div className="space-x-2">
                      <button
                        onClick={() => setCommissionPage((p) => Math.max(1, p - 1))}
                        disabled={commissionPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
                      >
                        Prev
                      </button>
                      <button
                        onClick={() => setCommissionPage((p) => (p < totalCommissionPages ? p + 1 : p))}
                        disabled={commissionPage >= totalCommissionPages}
                        className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {activeTab === 'withdrawal' && (
            <>
              {withdrawalsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-imola-primary mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading withdrawals...</p>
                </div>
              ) : (
                <>
                  {/* Filters for withdrawals */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 items-end">
                    <div className="md:col-span-2">
                      <label className="block text-sm text-gray-600 mb-1">Search</label>
                      <div className="flex items-center border border-gray-300 rounded-md px-3 py-2 bg-white">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                        </svg>
                        <input
                          type="text"
                          value={withdrawalSearch}
                          onChange={(e) => {
                            setWithdrawalSearch(e.target.value);
                            setWithdrawalPage(1);
                          }}
                          placeholder="Search by method..."
                          className="w-full ml-2 outline-none text-sm text-gray-800"
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1">
                        <label className="block text-sm text-gray-600 mb-1">Status</label>
                        <select
                          value={withdrawalStatusFilter}
                          onChange={(e) => {
                            setWithdrawalStatusFilter((e.target.value as 'ALL' | AffiliateWithdrawalStatus) || 'ALL');
                            setWithdrawalPage(1);
                          }}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary"
                        >
                          <option value="ALL">All</option>
                          <option value={AffiliateWithdrawalStatus.REQUESTED}>Requested</option>
                          <option value={AffiliateWithdrawalStatus.APPROVED}>Approved</option>
                          <option value={AffiliateWithdrawalStatus.PAID}>Paid</option>
                          <option value={AffiliateWithdrawalStatus.REJECTED}>Rejected</option>
                          <option value={AffiliateWithdrawalStatus.CANCELLED}>Cancelled</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm text-gray-600 mb-1">Page Size</label>
                        <select
                          value={withdrawalPageSize}
                          onChange={(e) => {
                            setWithdrawalPageSize(Number(e.target.value));
                            setWithdrawalPage(1);
                          }}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-imola-primary"
                        >
                          {[5, 10, 20, 50].map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Withdrawals Table */}
                  {filteredWithdrawals.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No withdrawal history available</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                            <th
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                              onClick={() => {
                                if (withdrawalStatusSort === null) {
                                  setWithdrawalStatusSort('asc');
                                  setWithdrawalDateSort('desc');
                                  setWithdrawalPaidAtSort(null);
                                } else if (withdrawalStatusSort === 'asc') {
                                  setWithdrawalStatusSort('desc');
                                } else {
                                  setWithdrawalStatusSort(null);
                                  setWithdrawalDateSort('desc');
                                }
                              }}
                            >
                              <span className="inline-flex items-center space-x-1">
                                <span>Status</span>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3 w-3 text-gray-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  {withdrawalStatusSort === 'asc' ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 15.75 12 9l6.75 6.75" />
                                  ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 8.25 12 15l6.75-6.75" />
                                  )}
                                </svg>
                              </span>
                            </th>
                            <th
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                              onClick={() => {
                                if (withdrawalPaidAtSort === null) {
                                  setWithdrawalPaidAtSort('asc');
                                  setWithdrawalStatusSort(null);
                                  setWithdrawalDateSort('desc');
                                } else if (withdrawalPaidAtSort === 'asc') {
                                  setWithdrawalPaidAtSort('desc');
                                } else {
                                  setWithdrawalPaidAtSort(null);
                                  setWithdrawalDateSort('desc');
                                }
                              }}
                            >
                              <span className="inline-flex items-center space-x-1">
                                <span>Paid At</span>
                                {withdrawalPaidAtSort !== null && (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-3 w-3 text-gray-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    {withdrawalPaidAtSort === 'asc' ? (
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 15.75 12 9l6.75 6.75" />
                                    ) : (
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 8.25 12 15l6.75-6.75" />
                                    )}
                                  </svg>
                                )}
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {pagedWithdrawals.map((withdrawal) => (
                          <tr key={withdrawal.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                              {withdrawal.id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-left">
                              {formatCurrency(Number(withdrawal.amount))}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                              {withdrawal.payoutMethod || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-left">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getWithdrawalStatusColor(withdrawal.status)}`}>
                                {getWithdrawalStatusDisplay(withdrawal.status)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                              {withdrawal.paidAt ? formatDate(withdrawal.paidAt) : '-'}
                            </td>
                          </tr>
                        ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Pagination withdrawals */}
                  <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
                    <div>
                      Showing {Math.min((withdrawalPage - 1) * withdrawalPageSize + 1, filteredWithdrawals.length)}-
                      {Math.min(withdrawalPage * withdrawalPageSize, filteredWithdrawals.length)} of {filteredWithdrawals.length}
                    </div>
                    <div className="space-x-2">
                      <button
                        onClick={() => setWithdrawalPage((p) => Math.max(1, p - 1))}
                        disabled={withdrawalPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
                      >
                        Prev
                      </button>
                      <button
                        onClick={() => setWithdrawalPage((p) => (p < totalWithdrawalPages ? p + 1 : p))}
                        disabled={withdrawalPage >= totalWithdrawalPages}
                        className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
         )}
        </div>
      </div>

      {/* Admin Notes */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 text-left">Admin Notes</h2>

          {/* Existing Notes */}
          {adminNotesLoading ? (
            <div className="text-center py-8 mb-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-imola-primary mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading notes...</p>
            </div>
          ) : (
          <div className="space-y-4 mb-6">
              {adminNotes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No admin notes available</p>
                </div>
              ) : (
                adminNotes.map((note) => (
              <div key={note.id} className="bg-gray-50 rounded-lg p-4 border-l-4 border-imola-primary">
                    <p className="text-sm text-gray-900 mb-2 text-left">{note.note}</p>
                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <span className="text-left">
                        By: <span className="font-medium">{note.admin?.name || 'Unknown'}</span>
                      </span>
                      <span className="text-right">
                        {new Date(note.createdAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true,
                        })}
                      </span>
                    </div>
              </div>
                ))
              )}
          </div>
          )}

          {/* Add New Note */}
          <div className="border-t border-gray-200 pt-6">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary mb-4"
              placeholder="Add a new administrative note..."
            />
            <div className="flex justify-end">
              <button
                onClick={handleSaveNote}
                disabled={savingNote || !newNote.trim()}
                className="bg-imola-primary hover:bg-imola-primary/80 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-2 rounded-md font-medium transition-colors"
              >
                {savingNote ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateMemberViewPage;
